import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  ErrorBoundary, FleetChrome, GlyphErrorPage, GlyphLoader, IconSprite, Loader,
  type NavigationConfig,
} from '@weretradeit/ui-react';
import { useAuth } from '@weretradeit/hooks';
import { ladeGlyphSzeneImLeerlauf } from './glyphScene';
import { captureAndPersistSsoToken, getCleanRedirectUrl } from './utils/auth';

// Early SSO token capture for cross-domain redirects (?session= or ?token=)
captureAndPersistSsoToken();

// Lazy load pages for code splitting
const HomePage = lazy(() => import('./pages/HomePage'));
const FlowDiagram = lazy(() => import('./pages/FlowDiagram'));
const GoalsPage = lazy(() => import('./pages/GoalsPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const WorkflowsPage = lazy(() => import('./pages/WorkflowsPage'));
const WwwSearchRadarPage = lazy(() => import('./pages/WwwSearchRadarPage'));
const CognitiveMemoryPage = lazy(() => import('./pages/CognitiveMemoryPage'));
const DependencyGraphPage = lazy(() => import('./pages/DependencyGraphPage'));
const VersionHistoryPage = lazy(() => import('./pages/VersionHistoryPage'));
const UsageAnalyticsPage = lazy(() => import('./pages/UsageAnalyticsPage'));

/** Schluessel in design-system v3/fleet-apps.json: Name, Marke, Art (internal), `mcp: false`. */
const APP_KEY = 'neuralWhale';

// Route type for hash-based routing
type Route = '/' | '/flows' | '/goals' | '/settings' | '/workflows' | '/www-search' | '/memory' | '/dependency-graph' | '/version-history' | '/usage-analytics';
const ROUTES: readonly Route[] = ['/', '/flows', '/goals', '/settings', '/workflows', '/www-search', '/memory', '/dependency-graph', '/version-history', '/usage-analytics'];

/**
 * Der Hash als Route — oder `null` fuer eine unbekannte Adresse (404).
 * Leerer Hash und `#/` sind der Katalog, wie bisher. Gelesen schon beim
 * ersten Zeichnen, nicht erst im Effekt: sonst blitzte bei `#/goals` einmal
 * der Katalog auf.
 *
 * Ein `?…` im Hash zaehlt nicht: public/assets/cache-buster.js laedt nach
 * einem Deploy mit `href + '?v=<Zeit>'` neu, und bei einer Hash-Adresse landet
 * das HINTER dem Hash (`#/goals?v=…`). Bis hierher fiel das auf den Katalog
 * zurueck (auch von `#/goals` aus); mit der 404 waere es die Fehlerseite.
 */
function routeFromHash(hash: string): Route | null {
  let h = hash.replace(/^#/, '').split('?')[0] || '/';
  if (!h.startsWith('/')) {
    h = '/' + h;
  }
  if (h.length > 1 && h.endsWith('/')) {
    h = h.slice(0, -1);
  }

  // Resilient route aliases
  if (h === '/www-search' || h === '/search' || h === '/radar' || h === '/watchlist' || h === '/www-explorer') return '/www-search';
  if (h === '/honcho' || h === '/brain') return '/memory';
  if (h === '/workflow' || h === '/samples' || h === '/orders' || h === '/sample-orders' || h === '/mail-actions' || h === '/inbox' || h === '/vendor-actions') return '/workflows';
  if (h === '/flow' || h === '/diagram') return '/flows';
  if (h === '/goal') return '/goals';
  if (h === '/setting') return '/settings';
  if (h === '/dependency' || h === '/deps' || h === '/graph') return '/dependency-graph';
  if (h === '/version' || h === '/history' || h === '/git') return '/version-history';
  if (h === '/analytics' || h === '/usage' || h === '/stats') return '/usage-analytics';

  return (ROUTES as readonly string[]).includes(h) ? (h as Route) : null;
}

/**
 * Die Navigation. Bis 4.2.1 standen hier Emoji vor den Beschriftungen
 * („📚 Catalog" …, Flotten-Regel H7). Jetzt traegt jede Zeile ein Symbol aus
 * dem Sprite — die Namen sind die aus design-system v3/icons/nav-icons.json
 * fuer genau diese Beschriftungen (Regel H10b), alle vier in names.json.
 *
 * `href` mit Hash statt `to`: der Anker zeigt auf `#/flows`, also oeffnet
 * „in neuem Tab" dieselbe Seite. `onNavigate` setzt den Hash selbst, damit
 * die mobile Schublade beim Klick schliesst.
 */
const NAVIGATION: NavigationConfig = {
  sections: [
    {
      items: [
        { label: 'Catalog', href: '#/', icon: 'book' },
        { label: 'Workflows', href: '#/workflows', icon: 'zap' },
        { label: 'Search Radar', href: '#/www-search', icon: 'search' },
        { label: 'Flow Diagrams', href: '#/flows', icon: 'flow' },
        { label: 'Goals', href: '#/goals', icon: 'target' },
        { label: 'Dependency Graph', href: '#/dependency-graph', icon: 'git-branch' },
        { label: 'Version History', href: '#/version-history', icon: 'history' },
        { label: 'Usage Analytics', href: '#/usage-analytics', icon: 'chart' },
        { label: 'Cognitive Memory', href: '#/memory', icon: 'sparkles' },
        { label: 'Settings', href: '#/settings', icon: 'settings' },
      ],
    },
  ],
};

/** Meldet einmal, dass die erste Seite steht (Effekt nach dem ersten Commit hinter Suspense). */
function ErsteSeiteSteht({ onSteht }: { onSteht: () => void }) {
  useEffect(() => { onSteht(); }, [onSteht]);
  return null;
}

function App() {
  const { user, profile, loading: authLoading, isAuthenticated } = useAuth({
    redirectOnUnauthenticated: true,
    redirectUrl: getCleanRedirectUrl(),
    loginUrl: 'https://login.weretrade.com/api/auth/sso',
    authEndpoint: 'https://login.weretrade.com/api/auth/me',
  });
  const [route, setRoute] = useState<Route | null>(() => routeFromHash(window.location.hash));
  const [ersteSeite, setErsteSeite] = useState(false);
  const markiereErsteSeite = useCallback(() => setErsteSeite(true), []);

  // Seamless SSO token synchronization:
  // When authenticated on .weretrade.com via cookie, but missing the cross-domain Bearer token in localStorage,
  // perform a one-time quick handshake with /api/auth/sso to obtain the signed token and persist it.
  useEffect(() => {
    if (isAuthenticated && !authLoading && typeof window !== 'undefined') {
      const existingToken = localStorage.getItem('wt_auth_token');
      if (!existingToken) {
        const attempted = sessionStorage.getItem('wt_sso_handshake_attempted');
        if (!attempted) {
          sessionStorage.setItem('wt_sso_handshake_attempted', '1');
          window.location.href =
            'https://login.weretrade.com/api/auth/sso?redirect=' +
            encodeURIComponent(getCleanRedirectUrl());
        }
      } else {
        sessionStorage.removeItem('wt_sso_handshake_attempted');
      }
    }
  }, [isAuthenticated, authLoading]);

  // Hash-based routing
  useEffect(() => {
    const handleHashChange = () => setRoute(routeFromHash(window.location.hash));
    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  /* Die GlyphField-Szene erst jetzt und erst im Leerlauf — nicht statisch aus
     main.tsx (accountingFox PR 790: die Engine nahm dem Aufbau der Huelle den
     Hauptthread). Begruendung in glyphScene.ts. */
  useEffect(() => {
    ladeGlyphSzeneImLeerlauf();
  }, []);

  /* Strikte Sitzungspruefung wie in accountingFox:
     Den Text „Session wird geprüft…" lesen die E2E-Fixtures der Flotte (shop-auth).
     Ist keine gueltige Sitzung vorhanden, leitet useAuth automatisch auf
     login.weretrade.com weiter und die App zeigt den Weiterleitungs-Loader. */
  if (authLoading) {
    return <GlyphLoader appKey={APP_KEY} label="Session wird geprüft…" />;
  }

  if (!isAuthenticated || !user) {
    return <GlyphLoader appKey={APP_KEY} label="Weiterleitung zu Login…" />;
  }

  // Render page based on route
  const renderPage = () => {
    switch (route) {
      case '/':
        return <HomePage />;
      case '/workflows':
        return <WorkflowsPage />;
      case '/www-search':
        return <WwwSearchRadarPage />;
      case '/flows':
        return <FlowDiagram />;
      case '/goals':
        return <GoalsPage />;
      case '/memory':
        return <CognitiveMemoryPage />;
      case '/settings':
        return <SettingsPage />;
      case '/dependency-graph':
        return <DependencyGraphPage />;
      case '/version-history':
        return <VersionHistoryPage />;
      case '/usage-analytics':
        return <UsageAnalyticsPage />;
      default:
        /* Die 404 der Flotte mit der eigenen Szene, INNERHALB der Huelle
           (Kopfzeile und Menue bleiben). Bis 4.2.1 fiel jede unbekannte
           Adresse still auf den Katalog zurueck — ein Tippfehler im Link sah
           aus wie ein Treffer. Das Gegenstueck zu `<Route path="*">` ist hier
           `route === null`. „Zur Startseite" bleibt im Hash (kein Neuladen). */
        return <GlyphErrorPage appKey={APP_KEY} homeHref="#/" />;
    }
  };

  /* Laden: die App hat keine eigene Sitzungspruefung (die Seiten fragen selbst,
     der Nutzerknopf holt `/me`), also ist der erste Ladebildschirm das Warten
     auf den ersten Seiten-Chunk — die Lage, in der es „sonst nichts zu zeigen"
     gibt (GLYPHFIELD.md § 3). Dort `GlyphLoader` mit `neuralWhale:login` ueber
     dem Fenster. Jeder spaetere Seitenwechsel wartet IM Inhalt, mit stehender
     Huelle: `<Loader>`. Bis 4.2.1 stand hier fuer beides `PageLoader`, ein
     eigener 50vh-Kasten um `LoadingOverlay` (in 3.0.0 entfallen) mit
     englischem Text. */
  const fallback = ersteSeite
    ? <Loader size="lg" label="Seite wird geladen" />
    : <GlyphLoader appKey={APP_KEY} label="neuralWhale wird geladen…" />;

  return (
    <>
      {/* Ohne den Sprite rendert jedes <Icon> als leeres <svg> — die
          Menuesymbole, der Launcher-Knopf, die Klapppfeile der Kopfzeile.
          /icons/sprite.svg kopiert scripts/copy-design-assets.mjs. */}
      <IconSprite />
      {/* DER Rahmen der Flotte (ui-react 4.0.0, DS-1): Staging-Streifen,
          Kopfzeile mit Marke und Name aus der Registry, Launcher · Sprache ·
          Thema · Umgebung · Nutzer, main.app-main, Fusszeile mit Rechtslinks.
          Ersetzt StagingBanner, AppLauncher (0.4er Props), AppHeader (variant,
          sticky, showThemeToggle, showUserWidget …), EnvironmentBadge und
          Footer sowie die Vanilla-Widgets aus index.html.

          Kein MCP-Punkt: die Registry fuehrt neuralWhale mit `mcp: false`
          (§18 — kein eigener MCP-Server).

          `hideInProduction`: hier stand `<EnvironmentBadge hideInProduction={true} />`.
          Die Vorgabe ist seit 3.0.0 umgedreht (0.4.0: false, 3.x: true,
          ui-react CHANGELOG/MIGRATION) — FleetChrome rendert das Abzeichen mit
          der Vorgabe, in Produktion also nichts. Wie bisher.

          Sitzung: noch keine App ist auf auth-client 2.2 (`auth={useAuth()}`),
          und App.tsx laedt keinen Nutzer — also `authBaseUrl`, der Knopf holt
          `/me` selbst, Abmelden laeuft eingebaut. login.weretrade.com wie bis
          hierher im Vanilla-Widget (auth.weretrade.com ist CF-challenged). */}
      <FleetChrome
        appKey={APP_KEY}
        navigation={NAVIGATION}
        activePath={route ? `#${route}` : undefined}
        onNavigate={(item) => { if (item.href) window.location.hash = item.href; }}
        mcp={{
          endpoint: '/api/mcp/health',
          pollMs: 30000,
        }}
        auth={{
          status: authLoading ? 'loading' : (isAuthenticated ? 'authenticated' : 'unauthenticated'),
          user: user ? {
            id: user.id,
            email: profile?.email || user.username,
            name: profile?.display_name || user.username,
            role: profile?.role || 'user',
            avatarUrl: profile?.avatar_url,
          } : null,
        }}
        authBaseUrl="https://login.weretrade.com/api/auth"
      >
        {/* Die Fehlergrenze der Seiten: `appKey`, OHNE eigenes `fallback` (ein
            `fallback` gewaenne in ErrorBoundary vor `appKey`). Ein Renderfehler
            zeichnet `GlyphErrorPage kind="500"` mit Kennung und „Erneut
            versuchen" — innerhalb der Huelle. `key` setzt die Grenze beim
            Seitenwechsel zurueck. Die aeussere Grenze um die Wurzel steht in
            main.tsx. */}
        <ErrorBoundary key={route ?? '404'} appKey={APP_KEY}>
          <Suspense fallback={fallback}>
            {renderPage()}
            {!ersteSeite && <ErsteSeiteSteht onSteht={markiereErsteSeite} />}
          </Suspense>
        </ErrorBoundary>
      </FleetChrome>
    </>
  );
}

export default App;
